import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { getPricingPlans, PricingPlan } from '../../services/omiseService';
import { CheckCircle, AlertCircle, ArrowLeft, Loader2, Package, Tag, Sparkles, Lightbulb, ChevronDown, CreditCard } from 'lucide-react';
import SEO from '../../components/SEO';
import PaymentMethodSelector from '../../components/PaymentMethodSelector';
import PromptPayQRPayment from '../../components/PromptPayQRPayment';
import { SUBSCRIPTION_PLANS, getPlanByTier } from '../../config/subscriptionPlans';

export default function SubscriptionCheckoutNew() {
  const [searchParams] = useSearchParams();
  const tier = searchParams.get('tier') as 'pro_monthly' | 'pro_semi_annual' | 'pro_yearly' | 'pro_plus_monthly' | 'pro_plus_semi_annual' | 'pro_plus_yearly';
  const source = searchParams.get('source'); // 'subscription' | 'salepage'
  const salePagePlan = searchParams.get('plan'); // 'basic' | 'premium'
  const salePageBilling = searchParams.get('billing'); // 'monthly' | 'halfyearly' | 'yearly'
  const salePageAmount = searchParams.get('amount'); // ราคา
  const { user } = useAuthContext();
  const { clearCart } = useCart();
  const navigate = useNavigate();

  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'qr' | 'card'>('qr');
  const [showPaymentMethods, setShowPaymentMethods] = useState(false); // ย่อส่วนวิธีชำระเงินไว้ก่อน กดแล้วค่อยขยาย
  const [chargeId, setChargeId] = useState<string>('');
  const [checkoutData, setCheckoutData] = useState<any>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [discountCode, setDiscountCode] = useState('');
  const [discount, setDiscount] = useState<any>(null);
  const [discountError, setDiscountError] = useState('');
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [activatingFree, setActivatingFree] = useState(false);
  const hasInitialized = useRef(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Scroll to top when data is loaded
  useEffect(() => {
    if (dataLoaded && !loading) {
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }, [dataLoaded, loading]);

  useEffect(() => {
    if (hasInitialized.current) return; // Prevent multiple loads
    hasInitialized.current = true;

    console.log('[Checkout] Starting checkout flow');
    console.log('[Checkout] URL source param:', source);
    console.log('[Checkout] User:', user ? 'Logged in' : 'Guest');

    // 1. อ่าน localStorage ก่อน
    const storedCheckoutData = localStorage.getItem('subscriptionCheckout');
    const storedSalePageData = localStorage.getItem('salePageCheckout');
    const storedCartData = localStorage.getItem('coursedeeCartCheckout');
    
    console.log('[Checkout] localStorage keys:', {
      subscriptionCheckout: !!storedCheckoutData,
      salePageCheckout: !!storedSalePageData,
      coursedeeCartCheckout: !!storedCartData
    });
    
    let tempCheckoutData = null;
    
    // 2. Parse data
    if (storedCartData) {
      try {
        tempCheckoutData = JSON.parse(storedCartData);
        setCheckoutData(tempCheckoutData);
        console.log('[Checkout] Loaded Coursedee Cart data:', tempCheckoutData);
      } catch (err) {
        console.error('Error parsing cart checkout data:', err);
      }
    } else if (storedSalePageData) {
      try {
        tempCheckoutData = JSON.parse(storedSalePageData);
        setCheckoutData(tempCheckoutData);
      } catch (err) {
        console.error('Error parsing salepage checkout data:', err);
      }
    } else if (storedCheckoutData) {
      try {
        tempCheckoutData = JSON.parse(storedCheckoutData);
        setCheckoutData(tempCheckoutData);
      } catch (err) {
        console.error('Error parsing checkout data:', err);
      }
    }
    
    // 3. ตรวจสอบว่าต้อง login หรือไม่
    const isRenewalOrUpgrade = tempCheckoutData?.isRenewal || tempCheckoutData?.isUpgrade || tempCheckoutData?.isAddon;
    const isNewPurchase = tempCheckoutData?.isNewPurchase || source === 'coursedee_cart' || source === 'salepage';
    
    console.log('[Checkout] Auth check:', { 
      user: !!user, 
      isRenewalOrUpgrade, 
      isNewPurchase,
      source,
      tempCheckoutData 
    });
    
    // 4. บังคับ login
    // - Renewal/Upgrade/Addon ต้อง login เสมอ
    // - Coursedee: "login ก่อนซื้อ" — ต้องล็อกอิน (Google) ก่อนเข้าหน้าชำระเงิน แล้วผูกคอร์สกับบัญชีเลย
    //   (ตัดหน้า "ยืนยันข้อมูลการสมัคร" ออก ไม่พึ่งระบบส่งเมล)
    // - salepage/edumi ยังอนุญาต guest checkout
    const mustLogin = !user && (!isNewPurchase || source === 'coursedee_cart');
    if (mustLogin) {
      console.log('[Checkout] Redirecting to login - authentication required');
      const redirectUrl = source === 'salepage'
        ? `/login?redirect=/subscription/checkout-new?source=salepage&plan=${salePagePlan}&billing=${salePageBilling}&amount=${salePageAmount}`
        : source === 'coursedee_cart'
        ? `/login?redirect=${encodeURIComponent('/subscription/checkout-new?source=coursedee_cart')}`
        : `/login?redirect=/subscription/checkout-new?tier=${tier}`;
      navigate(redirectUrl);
      return;
    }
    
    // 5. อนุญาต guest checkout สำหรับ new purchase หรือ coursedee_cart
    console.log('[Checkout] Proceeding with checkout - guest allowed');

    // 6. Load data immediately with tempCheckoutData
    loadData(tempCheckoutData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount - hasInitialized prevents multiple runs

  const loadData = async (initialData: any = null) => {
    // Prevent reloading if data is already loaded (e.g., when applying discount)
    if (dataLoaded) {
      console.log('[Checkout] Data already loaded, skipping reload');
      return;
    }
    
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      
      // Use initialData if provided, otherwise fall back to state
      const dataToCheck = initialData || checkoutData;
      
      // If this is from Coursedee Cart
      if (source === 'coursedee_cart' && dataToCheck?.isCoursedeeCart) {
        console.log('[Checkout] Coursedee Cart checkout detected');
        
        const customPlan: PricingPlan = {
          tier: 'coursedee_cart' as any,
          name: dataToCheck.planName,
          price: dataToCheck.total,
          billing_cycle: 'one_time' as any,
          features: {
            storage_gb: 0,
            unlimited_students: false,
            unlimited_courses: false,
            dashboard_analytics: false,
            seo_optimization: false,
            sale_page_builder: false
          }
        };
        
        setSelectedPlan(customPlan);
        setTotalAmount(customPlan.price);
        setDataLoaded(true);
        setLoading(false);
        return;
      }
      
      // If this is from Salepage, create custom plan
      if (source === 'salepage' && salePagePlan && salePageBilling && salePageAmount) {
        console.log('[Checkout] Salepage checkout detected');
        const planName = salePagePlan === 'basic' ? 'Basic' : 'Premium';
        const billingText = salePageBilling === 'monthly' ? 'รายเดือน' : salePageBilling === 'halfyearly' ? 'ราย 6 เดือน' : 'รายปี';
        
        const customPlan: PricingPlan = {
          tier: `salepage_${salePagePlan}_${salePageBilling}` as any,
          name: `${planName} (${billingText})`,
          price: parseInt(salePageAmount),
          billing_cycle: salePageBilling === 'yearly' ? 'yearly' : salePageBilling === 'halfyearly' ? 'semi_annual' : 'monthly',
          features: {
            storage_gb: 20,
            unlimited_students: true,
            unlimited_courses: true,
            dashboard_analytics: true,
            seo_optimization: false,
            sale_page_builder: false
          }
        };
        
        setSelectedPlan(customPlan);
        setTotalAmount(customPlan.price);
        setDataLoaded(true);
        setLoading(false);
        return;
      }
      
      const plansData = await getPricingPlans();
      setPlans(plansData);

      // Check for checkout data from localStorage
      const storedData = localStorage.getItem('subscriptionCheckout');
      let dataToUse = initialData || checkoutData;
      
      if (!dataToUse && storedData) {
        try {
          dataToUse = JSON.parse(storedData);
          console.log('[Checkout] Loaded data from localStorage:', dataToUse);
        } catch (err) {
          console.error('Error parsing stored checkout data:', err);
        }
      }
      
      console.log('[Checkout] dataToUse:', dataToUse);
      console.log('[Checkout] tier from URL:', tier);

      // If we have checkout data with valid plan info, use that
      if (dataToUse && dataToUse.planName && dataToUse.planName !== '') {
        console.log('[Checkout] Using checkout data:', dataToUse);
        
        // Check if this is URL Add-on only or Storage Add-on only purchase
        const isUrlAddonOnly = dataToUse.isUrlAddonOnly === true;
        const isStorageOnly = dataToUse.isStorageOnly === true;
        
        const customPlan: PricingPlan = {
          tier: dataToUse.plan || tier,
          name: isUrlAddonOnly ? 'URL Add-on' : isStorageOnly ? 'Storage Add-on' : dataToUse.planName,
          price: (isUrlAddonOnly || isStorageOnly) ? 0 : (dataToUse.planPrice || 0),
          billing_cycle: dataToUse.plan?.includes('yearly') ? 'yearly' : 'monthly',
          features: {
            storage_gb: 20,
            unlimited_students: true,
            unlimited_courses: true,
            dashboard_analytics: true,
            seo_optimization: dataToUse.plan?.includes('pro_plus'),
            sale_page_builder: dataToUse.plan?.includes('pro_plus')
          }
        };
        setSelectedPlan(customPlan);
        setCheckoutData(dataToUse);
        
        // Calculate total amount - if add-on only, use only the add-on price
        const total = (isUrlAddonOnly || isStorageOnly) ? (dataToUse.total || 0) : (dataToUse.total || dataToUse.planPrice || 0);
        setTotalAmount(total);
        console.log('[Checkout] Total amount set to:', total, 'isUrlAddonOnly:', isUrlAddonOnly, 'isStorageOnly:', isStorageOnly);
        
        // Remove localStorage after successfully using it
        localStorage.removeItem('subscriptionCheckout');
        localStorage.removeItem('salePageCheckout');
        localStorage.removeItem('coursedeeCartCheckout');
      } else if (tier) {
        console.log('[Checkout] Using tier from URL:', tier);
        
        // Handle Free plan
        if (tier === 'free') {
          const freePlan: PricingPlan = {
            tier: 'free' as any,
            name: 'FREE 14 วัน',
            price: 0,
            billing_cycle: 'one_time' as any,
            features: {
              storage_gb: 5,
              unlimited_students: false,
              unlimited_courses: false,
              dashboard_analytics: true,
              seo_optimization: false,
              sale_page_builder: false
            }
          };
          setSelectedPlan(freePlan);
          setTotalAmount(0);
          setDataLoaded(true);
          setLoading(false);
          return;
        }
        
        // Otherwise use tier from URL
        let plan = plansData.find(p => p.tier === tier);
        
        // Fallback: If API doesn't return the plan (e.g., semi-annual), use local config
        if (!plan) {
          console.log('[Checkout] Plan not found in API, using local config');
          const localPlan = getPlanByTier(tier);
          if (localPlan) {
            // Convert to PricingPlan format
            plan = {
              plan_id: tier,
              tier: tier as any,
              name: localPlan.name,
              price: localPlan.price,
              billing_cycle: tier.includes('yearly') ? 'yearly' : tier.includes('semi_annual') ? 'semi_annual' : 'monthly',
              currency: 'THB',
              features: {
                storage_gb: localPlan.features.storageGB.videoStorage / 1024,
                unlimited_students: localPlan.features.maxStudents === null,
                unlimited_courses: localPlan.features.maxCourses === null,
                dashboard: localPlan.features.hasDashboard,
                seo_optimization: (localPlan.features as any).seoOptimization || false,
                sale_page_builder: (localPlan.features as any).salePageBuilder || false
              }
            };
          }
        }
        
        if (plan) {
          setSelectedPlan(plan);
          setTotalAmount(plan.price);
        } else {
          setError('ไม่พบแพ็คเกจที่เลือก');
        }
      } else {
        // No tier and no checkout data
        const hn = window.location.hostname;
        const isCoursedeeHost = source === 'coursedee_cart' || hn.includes('aiforyou');
        if (isCoursedeeHost) {
          // Coursedee ไม่ใช้หน้า renew ของ edumi — กลับ dashboard คอร์สดี
          console.log('[Checkout] No data on coursedee host, redirecting to coursedee dashboard');
          navigate('/coursedee/dashboard');
        } else {
          console.log('[Checkout] No tier or checkout data found, redirecting to renew page');
          navigate('/subscription/renew');
        }
        return;
      }
      
      setDataLoaded(true);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError('ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setLoading(false);
    }
  };

  const applyDiscount = async () => {
    if (!discountCode.trim()) return;
    
    setApplyingDiscount(true);
    setDiscountError('');
    
    try {
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const code = discountCode.toUpperCase().trim();
      let discountDoc = null;
      let discountData: any = null;
      let collectionName = '';
      
      // Try subscription_discounts first
      const q1 = query(
        collection(db, 'subscription_discounts'),
        where('code', '==', code),
        where('isActive', '==', true)
      );
      
      const snapshot1 = await getDocs(q1);
      
      if (!snapshot1.empty) {
        discountDoc = snapshot1.docs[0];
        discountData = discountDoc.data();
        collectionName = 'subscription_discounts';
        
        // Check if discount is applicable for subscription discounts
        if (discountData.applicableTo !== 'all' && discountData.applicableTo !== 'subscription') {
          setDiscountError('รหัสส่วนลดนี้ใช้ไม่ได้กับการสมัครสมาชิก');
          setApplyingDiscount(false);
          return;
        }
      } else {
        // Try salePageDiscounts if not found in subscription_discounts
        const q2 = query(
          collection(db, 'salePageDiscounts'),
          where('code', '==', code),
          where('isActive', '==', true)
        );
        
        const snapshot2 = await getDocs(q2);
        
        if (!snapshot2.empty) {
          discountDoc = snapshot2.docs[0];
          discountData = snapshot2.docs[0].data();
          collectionName = 'salePageDiscounts';
        } else {
          setDiscountError('รหัสส่วนลดไม่ถูกต้องหรือหมดอายุแล้ว');
          setApplyingDiscount(false);
          return;
        }
      }
      
      // Check usage limit (for subscription_discounts)
      if (collectionName === 'subscription_discounts' && discountData.usageLimit && discountData.usedCount >= discountData.usageLimit) {
        setDiscountError('รหัสส่วนลดนี้ถูกใช้งานครบจำนวนแล้ว');
        setApplyingDiscount(false);
        return;
      }
      
      // Check validity dates
      const now = new Date();
      
      // For subscription_discounts
      if (collectionName === 'subscription_discounts') {
        if (discountData.validFrom && discountData.validFrom.toDate() > now) {
          setDiscountError('รหัสส่วนลดนี้ยังไม่เริ่มใช้งาน');
          setApplyingDiscount(false);
          return;
        }
        
        if (discountData.validUntil && discountData.validUntil.toDate() < now) {
          setDiscountError('รหัสส่วนลดนี้หมดอายุแล้ว');
          setApplyingDiscount(false);
          return;
        }
      }
      
      // For salePageDiscounts
      if (collectionName === 'salePageDiscounts' && discountData.expirationDate) {
        const expirationDate = discountData.expirationDate.toDate ? discountData.expirationDate.toDate() : new Date(discountData.expirationDate);
        if (expirationDate < now) {
          setDiscountError('รหัสส่วนลดนี้หมดอายุแล้ว');
          setApplyingDiscount(false);
          return;
        }
      }
      
      // Apply discount
      setDiscount({
        id: discountDoc.id,
        collection: collectionName,
        ...discountData
      });
      
      // Recalculate total with discount
      const currentTotal = totalAmount > 0 ? totalAmount : selectedPlan?.price || 0;
      let newTotal = 0;
      
      // Handle different discount formats
      if (collectionName === 'subscription_discounts') {
        // subscription_discounts uses 'type' and 'value'
        if (discountData.type === 'percentage') {
          // Calculate final amount directly from percentage to avoid rounding issues
          newTotal = Math.floor(currentTotal * (100 - discountData.value) / 100);
        } else {
          // Fixed amount discount
          newTotal = Math.max(0, currentTotal - discountData.value);
        }
      } else {
        // salePageDiscounts uses 'percentage'
        // Calculate final amount directly from percentage to avoid rounding issues
        newTotal = Math.floor(currentTotal * (100 - discountData.percentage) / 100);
      }
      
      setTotalAmount(newTotal);
      
    } catch (error) {
      console.error('Error applying discount:', error);
      setDiscountError('เกิดข้อผิดพลาดในการตรวจสอบรหัสส่วนลด');
    } finally {
      setApplyingDiscount(false);
    }
  };

  const removeDiscount = () => {
    setDiscount(null);
    setDiscountCode('');
    setDiscountError('');
    // Recalculate total without discount
    const originalTotal = checkoutData?.total || selectedPlan?.price || 0;
    setTotalAmount(originalTotal);
  };

  const handlePaymentSuccess = async (newChargeId: string) => {
    console.log('[Checkout] Payment successful, charge ID:', newChargeId);
    setChargeId(newChargeId);
    
    // Clear cart immediately for Coursedee Cart purchases
    if (source === 'coursedee_cart') {
      clearCart(); // Clear React state
      localStorage.removeItem('coursedeeCartCheckout');
      localStorage.removeItem('edumi_cart');
      console.log('[Checkout] Cart cleared after payment success');
    }
    
    // Check if this is a new purchase (guest checkout)
    const isNewPurchase = checkoutData?.isNewPurchase || source === 'coursedee_cart' || source === 'salepage';
    const isRenewalOrUpgrade = checkoutData?.isRenewal || checkoutData?.isUpgrade || checkoutData?.isAddon;
    const isGuestCheckout = !user && isNewPurchase;
    
    console.log('[Checkout v3.1] Payment success routing:', { 
      isNewPurchase, 
      isGuestCheckout, 
      user: !!user,
      userObj: user,
      source,
      checkoutDataSource: checkoutData?.source
    });
    
    // If this is guest checkout (no user), redirect to RegisterInfo
    if (isGuestCheckout) {
      // Save discount info to localStorage before redirecting
      const originalAmount = selectedPlan?.price || 0;
      const discountPercentage = discount ? (discount.value || discount.percentage || 0) : 0;
      const discountAmount = discount ? (originalAmount - totalAmount) : 0;
      
      const checkoutDataWithDiscount = {
        ...checkoutData,
        originalAmount: originalAmount,
        finalAmount: totalAmount,
        discountCode: discount ? discountCode.toUpperCase().trim() : null,
        discountPercent: discountPercentage,
        discountAmount: discountAmount,
        paymentId: newChargeId
      };
      
      // Save to correct localStorage key
      if (source === 'coursedee_cart' || source === 'coursedee') {
        localStorage.setItem('coursedeeCartCheckout', JSON.stringify(checkoutDataWithDiscount));
      } else if (source === 'salepage') {
        localStorage.setItem('salePageCheckout', JSON.stringify(checkoutDataWithDiscount));
      } else {
        localStorage.setItem('subscriptionCheckout', JSON.stringify(checkoutDataWithDiscount));
      }
      
      setSuccess(true);
      setTimeout(() => {
        // Determine source domain
        const sourceDomain = source === 'coursedee_cart' ? 'coursedee' : source || 'edumi';
        // For Coursedee Cart, get tier from items[0].version
        let tier = selectedPlan.tier;
        if (source === 'coursedee_cart' && checkoutData?.items?.[0]?.version) {
          tier = checkoutData.items[0].version;
          console.log('[Checkout v3.0] Using tier from items:', tier);
        }
        console.log('[Checkout v3.0] Final tier:', tier, 'selectedPlan.tier:', selectedPlan.tier);
        navigate(`/subscription/register-info?payment=${newChargeId}&plan=${tier}&amount=${totalAmount}&source=${sourceDomain}`);
      }, 2000);
      return;
    }
    
    // Coursedee Cart purchase (logged in) → ผูกคอร์สกับบัญชีที่ล็อกอินอยู่เลย แล้วไป dashboard
    // (login-before-buy: user ต้องล็อกอินก่อนถึงหน้านี้แล้ว จึงไม่ต้องผ่านหน้า RegisterInfo)
    if (source === 'coursedee_cart' && checkoutData?.isCoursedeeCart && user) {
      try {
        const { db } = await import('../../lib/firebase');
        const { doc, getDoc, updateDoc, addDoc, collection, serverTimestamp, arrayUnion, Timestamp } = await import('firebase/firestore');

        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        const uData = userSnap.data();
        const originalAmount = checkoutData?.total || selectedPlan?.price || 0;

        const items = checkoutData.items.map((item: any) => ({
          id: item.id,
          name: item.name,
          version: item.version,
          price: item.price || 0,
          finalPrice: item.price || 0
        }));

        // 1. Payment record
        await addDoc(collection(db, 'payments'), {
          paymentId: newChargeId,
          userId: user.uid,
          email: uData?.email || user.email,
          fullName: uData?.fullName || uData?.displayName || user.displayName || 'User',
          phone: uData?.phone || '',
          items,
          totalAmount: totalAmount,
          originalAmount,
          discountCode: discount ? discountCode.toUpperCase().trim() : null,
          status: 'verified',
          source: 'coursedee_cart',
          paymentMethod: 'promptpay',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

        // 2. Attach packages to user
        const coursedeePackages = checkoutData.items.map((item: any) => ({
          id: item.id,
          name: item.name,
          version: item.version,
          price: item.price,
          purchaseDate: Timestamp.now(), // serverTimestamp() ใช้ใน arrayUnion ไม่ได้
          paymentId: newChargeId
        }));
        await updateDoc(userRef, {
          coursedeePackages: arrayUnion(...coursedeePackages),
          updatedAt: serverTimestamp()
        });

        // 3. Purchase history record
        await addDoc(collection(db, 'subscription_payments'), {
          userId: user.uid,
          email: uData?.email || user.email,
          amount: totalAmount,
          originalAmount,
          items,
          discountCode: discount ? discountCode.toUpperCase().trim() : null,
          status: 'verified',
          source: 'coursedee_cart',
          paymentMethod: 'promptpay',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

        console.log('[Checkout] Coursedee paid purchase attached to user:', user.uid);
      } catch (err) {
        console.error('[Checkout] Error attaching coursedee purchase:', err);
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/coursedee/dashboard?payment=success');
      }, 2000);
      return;
    }
    
    // If this is Sale Page purchase (logged in), redirect to success page
    if (source === 'salepage' && user) {
      setSuccess(true);
      setTimeout(() => {
        navigate(`/salepage/checkout-success?payment_id=${newChargeId}`);
      }, 2000);
      return;
    }
    
    // If this is EduMi purchase (logged in), redirect to success page
    if (!source && user) {
      setSuccess(true);
      setTimeout(() => {
        navigate(`/edumi/checkout-success?payment_id=${newChargeId}`);
      }, 2000);
      return;
    }
    
    // If this is URL Add-on only purchase, process it
    if (checkoutData?.isUrlAddonOnly && checkoutData?.urlAddons && user) {
      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions();
        const processUrlAddonPayment = httpsCallable(functions, 'processUrlAddonPayment');
        
        const result = await processUrlAddonPayment({
          userId: user.uid,
          tier: checkoutData.plan,
          urlAddons: checkoutData.urlAddons,
          amount: totalAmount,
          paymentMethod: paymentMethod,
          chargeId: newChargeId,
          proRateInfo: checkoutData.proRateInfo
        });
        
        console.log('[Checkout] URL Add-on processed:', result);
      } catch (error) {
        console.error('[Checkout] Error processing URL Add-on:', error);
      }
    }
    
    setSuccess(true);
    
    // For renewal/upgrade, redirect to dashboard
    setTimeout(() => {
      if (source === 'salepage') {
        navigate(`/salepage/dashboard?payment=success`);
      } else {
        navigate(`/payment/omise/success?charge_id=${newChargeId}&type=subscription`);
      }
    }, 2000);
  };

  const handlePaymentError = (errorMessage: string) => {
    console.error('[Checkout] Payment error:', errorMessage);
    setError(errorMessage);
  };

  const handleFreeActivation = async () => {
    if (!selectedPlan) {
      console.error('[Checkout] No plan selected');
      setError('กรุณาเลือกแพ็คเกจก่อน');
      return;
    }
    
    // If no user, redirect to guest checkout page (same as payment flow)
    if (!user) {
      console.log('[Checkout] No user - redirecting to guest checkout for free activation');
      // Save checkout data for guest checkout page
      const originalAmount = selectedPlan.price;
      const discountPercentage = discount ? (discount.value || discount.percentage || 100) : null;
      const guestCheckoutData: any = {
        tier: selectedPlan.tier,
        amount: 0,
        originalAmount: originalAmount,
        finalAmount: 0,
        discountCode: discount ? discountCode.toUpperCase().trim() : null,
        discountPercent: discountPercentage,
        discountAmount: originalAmount,
        paymentMethod: 'free_activation',
        isNewPurchase: true,
        isFreeActivation: true,
      };
      
      // For Coursedee Cart: Include items and metadata
      if (source === 'coursedee_cart' && checkoutData?.items) {
        guestCheckoutData.items = checkoutData.items;
        guestCheckoutData.isCoursedeeCart = true;
        guestCheckoutData.itemCount = checkoutData.itemCount;
        guestCheckoutData.planName = checkoutData.planName;
      }
      
      // Save to correct localStorage key based on source
      if (source === 'coursedee_cart' || source === 'coursedee') {
        localStorage.setItem('coursedeeCartCheckout', JSON.stringify(guestCheckoutData));
      } else {
        localStorage.setItem('subscriptionCheckout', JSON.stringify(guestCheckoutData));
      }
      
      // Determine tier and source for URL
      const sourceDomain = source === 'coursedee_cart' ? 'coursedee' : source || 'edumi';
      let tier = selectedPlan.tier;
      
      // For Coursedee Cart, get tier from items[0].version if available
      if (source === 'coursedee_cart' && checkoutData?.items?.[0]?.version) {
        tier = checkoutData.items[0].version;
        console.log('[Checkout] Free activation - using tier from items:', tier);
      }
      
      navigate(`/subscription/register-info?plan=${tier}&amount=0&source=${sourceDomain}`);
      return;
    }
    
    // Check if this is Free plan or 100% discount
    const isFreeplan = totalAmount === 0 && !discount;
    const is100PercentDiscount = discount && discount.value === 100;
    
    if (!isFreeplan && !is100PercentDiscount) {
      setError('รหัสส่วนลดไม่ถูกต้องหรือไม่ใช่ส่วนลด 100%');
      return;
    }
    
    setActivatingFree(true);
    setError('');
    
    try {
      console.log('[Checkout] Starting free activation for user:', user.uid);
      
      const { db } = await import('../../lib/firebase');
      const { doc, getDoc, updateDoc, addDoc, collection, serverTimestamp, arrayUnion, increment, Timestamp } = await import('firebase/firestore');
      
      // 1. Validate discount code (only if using discount)
      let discountData: any = null;
      if (discount) {
        const discountRef = doc(db, 'subscription_discounts', discount.id);
        const discountDoc = await getDoc(discountRef);
      
        if (!discountDoc.exists()) {
          throw new Error('รหัสส่วนลดไม่ถูกต้อง');
        }
        
        discountData = discountDoc.data();
        
        if (!discountData.isActive) {
          throw new Error('รหัสส่วนลดไม่ได้เปิดใช้งาน');
        }
        
        if (discountData.type !== 'percentage' || discountData.value !== 100) {
          throw new Error('รหัสส่วนลดนี้ไม่ใช่ส่วนลด 100%');
        }
        
        if (discountData.usageLimit && discountData.usedCount >= discountData.usageLimit) {
          throw new Error('รหัสส่วนลดถูกใช้งานครบจำนวนแล้ว');
        }
        
        // Allow users to use the code multiple times for upgrades
        // Removed usedBy check to allow subscription upgrades
      }
      
      // 2. Get current subscription end date
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      
      // Calculate end date from current end date or now
      const calculateEndDate = (tier: string): Date => {
        let startDate = new Date();
        
        // If user has existing subscription that hasn't expired, extend from that date
        if (userData?.subscriptionEndDate) {
          const currentEndDate = userData.subscriptionEndDate.toDate();
          if (currentEndDate > new Date()) {
            startDate = currentEndDate;
          }
        }
        
        if (tier.includes('yearly')) {
          return new Date(startDate.setFullYear(startDate.getFullYear() + 1));
        } else if (tier.includes('semi_annual')) {
          return new Date(startDate.setMonth(startDate.getMonth() + 6));
        } else {
          return new Date(startDate.setMonth(startDate.getMonth() + 1));
        }
      };
      
      // Check if this is Storage Add-on purchase
      const isStorageAddon = checkoutData?.isStorageOnly === true;
      
      if (isStorageAddon) {
        // For Storage Add-on, record as addon purchase
        const storageGB = checkoutData?.storageAddon || 0;
        
        await addDoc(collection(db, 'paymentLogs'), {
          userId: user.uid,
          tier: userData?.subscriptionTier || 'free',
          amount: 0,
          originalAmount: checkoutData?.total || 0,
          discountCode: discount ? discountCode.toUpperCase().trim() : null,
          discountPercent: discount ? 100 : null,
          paymentMethod: 'free_activation',
          status: 'completed',
          type: 'storage_addon',
          storageAddon: storageGB,
          timestamp: serverTimestamp()
        });
        
        // Update user's storage addons
        const currentAddons = userData?.storageAddons || [];
        const newAddon = {
          storageGB: storageGB,
          quantity: 1,
          purchaseDate: Timestamp.now(),
          paymentMethod: 'free_activation'
        };
        
        // Append new addon to existing array
        const updatedAddons = [...currentAddons, newAddon];
        
        await updateDoc(userRef, {
          storageAddons: updatedAddons,
          updatedAt: serverTimestamp()
        });
      } else {
        // Check if this is Coursedee Cart purchase
        const isCoursedeeCart = source === 'coursedee_cart' && checkoutData?.isCoursedeeCart;
        
        if (isCoursedeeCart) {
          // For Coursedee Cart: Create payment record in payments collection
          const originalAmount = checkoutData?.total || selectedPlan?.price || 0;
          const paymentId = `FREE_${user.uid}_${Date.now()}`;
          
          const items = checkoutData.items.map((item: any) => ({
            id: item.id,
            name: item.name,
            version: item.version,
            price: item.price || 0,
            finalPrice: 0 // 100% discount
          }));
          
          await addDoc(collection(db, 'payments'), {
            paymentId: paymentId,
            userId: user.uid,
            email: userData?.email || user.email,
            fullName: userData?.fullName || userData?.displayName || 'User',
            phone: userData?.phone || '',
            items: items,
            totalAmount: 0,
            originalAmount: originalAmount,
            discountCode: discount ? discountCode.toUpperCase().trim() : null,
            status: 'verified',
            source: 'coursedee_cart',
            paymentMethod: 'free_activation',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          
          // Update user's coursedeePackages
          const coursedeePackages = checkoutData.items.map((item: any) => ({
            id: item.id,
            name: item.name,
            version: item.version,
            price: item.price,
            purchaseDate: Timestamp.now(), // serverTimestamp() ใช้ใน arrayUnion ไม่ได้ ต้องใช้ client timestamp
            paymentId: paymentId
          }));
          
          await updateDoc(userRef, {
            coursedeePackages: arrayUnion(...coursedeePackages),
            updatedAt: serverTimestamp()
          });
          
          console.log('[Checkout] Created payment record for Coursedee Cart with 100PER discount');
        } else {
          // For regular subscription
          const endDate = calculateEndDate(selectedPlan.tier);
          
          // 3. Update user subscription
          await updateDoc(userRef, {
            subscriptionTier: selectedPlan.tier,
            subscriptionEndDate: Timestamp.fromDate(endDate),
            subscriptionStatus: 'active',
            subscriptionStartDate: userData?.subscriptionStartDate || serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          
          // 4. Create payment log
          await addDoc(collection(db, 'paymentLogs'), {
            userId: user.uid,
            tier: selectedPlan.tier,
            amount: 0,
            originalAmount: selectedPlan.price,
            discountCode: discount ? discountCode.toUpperCase().trim() : null,
            discountPercent: discount ? 100 : null,
            paymentMethod: 'free_activation',
            status: 'completed',
            type: 'subscription',
            endDate: Timestamp.fromDate(endDate),
            timestamp: serverTimestamp()
          });
        }
      }
      
      // 5. Update discount code usage (only if using discount)
      if (discount && discountData) {
        const isFirstTimeUse = !discountData.usedBy || !discountData.usedBy.includes(user.uid);
        const updateData: any = {
          usedBy: arrayUnion(user.uid),
          updatedAt: serverTimestamp()
        };
        
        // Only increment count on first use to prevent abuse
        if (isFirstTimeUse) {
          updateData.usedCount = increment(1);
        }
        
        const discountRef = doc(db, 'subscription_discounts', discount.id);
        await updateDoc(discountRef, updateData);
      }
      
      // 6. Create notification
      const notificationMessage = isStorageAddon 
        ? (discount ? `คุณได้รับพื้นที่เพิ่มเติม ${checkoutData?.storageAddon} GB ฟรีด้วยโค้ดส่วนลด 100%` : `คุณได้รับพื้นที่เพิ่มเติม ${checkoutData?.storageAddon} GB`)
        : (discount ? `คุณได้เปิดใช้งานแพ็คเก็จ ${selectedPlan.name} ฟรีด้วยโค้ดส่วนลด 100%` : `คุณได้เปิดใช้งานแพ็คเก็จ ${selectedPlan.name} ฟรี 14 วัน`);
      
      await addDoc(collection(db, 'notifications'), {
        userId: user.uid,
        type: isStorageAddon ? 'storage_addon_purchased' : 'subscription_activated',
        title: 'เปิดใช้งานสำเร็จ!',
        message: notificationMessage,
        read: false,
        createdAt: serverTimestamp()
      });
      
      console.log('[Checkout] Free activation successful');

      // เคลียร์ตะกร้าเหมือน path จ่ายเงิน (handlePaymentSuccess) ไม่งั้นสินค้าค้างในตะกร้าหลังใช้โค้ด 100%
      if (source === 'coursedee_cart') {
        clearCart(); // อัปเดต state ใน CartContext
        localStorage.removeItem('coursedeeCartCheckout');
        localStorage.removeItem('edumi_cart');
        console.log('[Checkout] Cart cleared after free activation');
      }

      setSuccess(true);

      setTimeout(() => {
        if (source === 'salepage') {
          navigate('/salepage/dashboard?payment=success');
        } else if (source === 'coursedee_cart' && checkoutData?.isCoursedeeCart) {
          // For Coursedee Cart, redirect to Coursedee Dashboard
          navigate('/coursedee/dashboard?payment=success');
        } else if (isStorageAddon) {
          // For Storage Add-on, go directly to payment history
          navigate('/payment/history?success=true');
        } else {
          navigate('/payment/omise/success?type=free_activation');
        }
      }, 2000);
    } catch (error: any) {
      console.error('[Checkout] Free activation error:', error);
      setError(error.message || 'เกิดข้อผิดพลาดในการเปิดใช้งาน กรุณาลองใหม่อีกครั้ง');
    } finally {
      setActivatingFree(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F3F8F8] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#00B4A1] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#F3F8F8] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle className="w-16 h-16 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">ชำระเงินสำเร็จ!</h2>
          <p className="text-gray-600 mb-4">
            การสมัครสมาชิกของคุณเสร็จสมบูรณ์แล้ว
          </p>
          <p className="text-sm text-gray-500">กำลังเปลี่ยนเส้นทาง...</p>
        </div>
      </div>
    );
  }

  if (!selectedPlan) {
    return (
      <div className="min-h-screen bg-[#F3F8F8] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-xl text-gray-900 mb-4">{error || 'ไม่พบแพ็กเกจที่เลือก'}</p>
          <button
            onClick={() => navigate(source === 'salepage' ? '/salepage/billing' : '/subscription/renew')}
            className="px-6 py-3 bg-gradient-to-r from-[#00B4A1] to-[#3A93B9] text-white rounded-xl font-semibold hover:shadow-xl transition-all"
          >
            กลับไปเลือกแพ็กเกจใหม่
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`ชำระเงิน ${selectedPlan.name} - EduMi`}
        description={`ชำระเงินสำหรับแพ็กเกจ ${selectedPlan.name} ราคา ${selectedPlan.price} บาท`}
        noindex={true}
      />
      <div className="min-h-screen bg-[#F3F8F8] py-6 md:py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4 md:mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>ย้อนกลับ</span>
          </button>

          <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2 text-center">
            ชำระเงิน {selectedPlan.name}{source === 'salepage' ? ' (Salepage)' : ''}
          </h1>
          <p className="text-center text-gray-600 text-sm md:text-base mb-6 md:mb-8">
            {source === 'salepage' 
              ? 'ชำระเงินสำหรับ Sale Page Builder - เลือกวิธีการชำระเงินที่สะดวกสำหรับคุณ'
              : 'เลือกวิธีการชำระเงินที่สะดวกสำหรับคุณ'}
          </p>

          <div className="grid lg:grid-cols-3 gap-5 lg:gap-8">
            {/* Right Column - Order Summary (Show first on mobile) */}
            <div className="lg:col-span-1 lg:order-2">
              <div className="bg-white rounded-2xl shadow-xl p-6 lg:sticky lg:top-4">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-[#00B4A1]" />
                  สรุปการสั่งซื้อ
                </h3>
                
                <div className="space-y-3 mb-4 pb-4 border-b">
                  {/* Show Coursedee Cart Items */}
                  {checkoutData?.isCoursedeeCart && checkoutData?.items ? (
                    <>
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-gray-700 mb-2">รายการคอร์ส ({checkoutData.itemCount || checkoutData.items.length} คอร์ส)</p>
                        {checkoutData.items.map((item: any, index: number) => (
                          <div key={index} className="flex items-start justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-gray-900 leading-snug">{item.name}</p>
                              {item.description && (
                                <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                              )}
                            </div>
                            <span className="font-semibold text-[#00B4A1] whitespace-nowrap flex-shrink-0">฿{item.price.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : !checkoutData?.isUrlAddonOnly && !checkoutData?.isStorageOnly && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">แพ็กเกจ</span>
                        <span className="font-semibold text-gray-900">
                          {selectedPlan.name}{source === 'salepage' ? ' (Salepage)' : ''}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">รอบบิล</span>
                        <span className="font-semibold text-gray-900">
                          {selectedPlan.billing_cycle === 'yearly' ? 'รายปี' : selectedPlan.billing_cycle === 'one_time' ? 'ครั้งเดียว' : 'รายเดือน'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">ราคาแพ็กเกจ</span>
                        <span className="font-semibold text-gray-900">฿{selectedPlan.price.toLocaleString()}</span>
                      </div>
                    </>
                  )}
                  
                  {/* Show Storage Add-on info if present */}
                  {checkoutData?.isStorageOnly && checkoutData?.storageAddon && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">แพ็กเกจ</span>
                        <span className="font-semibold text-gray-900">Storage Add-on</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">รอบบิล</span>
                        <span className="font-semibold text-gray-900">รายปี</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">ราคาแพ็กเกจ</span>
                        <span className="font-semibold text-gray-900">฿0</span>
                      </div>
                      <div className="flex justify-between items-center bg-orange-50 -mx-3 px-3 py-2 rounded-lg">
                        <span className="text-gray-700 font-medium">จำนวนพื้นที่</span>
                        <span className="font-bold text-orange-600 text-lg">+{checkoutData.storageAddon} GB</span>
                      </div>
                    </>
                  )}
                  
                  {/* Show URL addons if present */}
                  {checkoutData?.urlAddons && checkoutData.urlAddons.length > 0 && (
                    <>
                      {checkoutData.isUrlAddonOnly && checkoutData.proRateInfo ? (
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">URL Add-on (Pro-rate)</span>
                            <span className="font-semibold text-[#5B6FCB]">฿{checkoutData.proRateInfo.proRatedPrice.toLocaleString()}</span>
                          </div>
                          <div className="text-xs text-gray-500 pl-4">
                            • {checkoutData.urlAddons.map((a: any) => a.type === 'path' ? 'Path URL' : 'Subdomain').join(' + ')}
                          </div>
                          <div className="text-xs text-gray-500 pl-4">
                            • ระยะเวลาที่เหลือ: {checkoutData.proRateInfo.daysRemaining} วัน
                          </div>
                          <div className="text-xs text-gray-400 line-through pl-4">
                            ราคาเต็มรอบ: ฿{checkoutData.proRateInfo.originalPrice.toLocaleString()}
                          </div>
                        </div>
                      ) : (
                        checkoutData.urlAddons.map((addon: any, index: number) => {
                          const isYearly = checkoutData.plan?.includes('yearly');
                          const isSemiAnnual = checkoutData.plan?.includes('semi_annual');
                          let price = 0;
                          
                          if (addon.type === 'path') {
                            price = isYearly ? 490 : isSemiAnnual ? 245 : 49;
                          } else if (addon.type === 'subdomain') {
                            const isProPlusYearly = checkoutData.plan === 'pro_plus_yearly';
                            if (!isProPlusYearly) {
                              price = isYearly ? 990 : isSemiAnnual ? 495 : 99;
                            }
                          }
                          
                          return price > 0 ? (
                            <div key={index} className="flex justify-between">
                              <span className="text-gray-600">
                                {addon.type === 'path' ? 'Path URL' : 'Subdomain'}
                              </span>
                              <span className="font-semibold text-[#5B6FCB]">฿{price.toLocaleString()}</span>
                            </div>
                          ) : null;
                        })
                      )}
                    </>
                  )}
                </div>

                {/* Show discount if applied */}
                {discount && (
                  <div className="mb-4 pb-4 border-b">
                    <div className="flex justify-between text-green-600">
                      <span className="font-semibold">ส่วนลด ({discount.code})</span>
                      <span className="font-bold">
                        -{discount.type === 'percentage' 
                          ? `฿${Math.round((checkoutData?.planPrice || selectedPlan?.price || 0) * discount.value / 100).toLocaleString()} (${discount.value}%)`
                          : discount.percentage
                          ? `฿${Math.round((checkoutData?.planPrice || selectedPlan?.price || 0) * discount.percentage / 100).toLocaleString()} (${discount.percentage}%)`
                          : `฿${(discount.value || 0).toLocaleString()}`
                        }
                      </span>
                    </div>
                  </div>
                )}

                {/* Total */}
                <div className="flex justify-between items-center gap-3 mb-6">
                  <span className="text-lg md:text-xl font-bold text-gray-900 flex-shrink-0">ยอดรวม</span>
                  <span className="text-2xl md:text-3xl font-bold text-[#00B4A1] text-right break-all">
                    ฿{(totalAmount || 0).toLocaleString()}
                  </span>
                </div>

                {/* Features List — เฉพาะ edumi/salepage (Coursedee ไม่เกี่ยว จึงซ่อน) */}
                {!checkoutData?.isCoursedeeCart && (
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-700 mb-3">สิ่งที่คุณจะได้รับ</p>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">พื้นที่จัดเก็บ 20 GB</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">นักเรียนไม่จำกัด</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">คอร์สไม่จำกัด</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">Dashboard และ Analytics</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Left Column - Payment Method & Details (Show second on mobile) */}
            <div className="lg:col-span-2 lg:order-1">
              <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
                {/* Collapsible header — ย่อไว้ก่อน กดแล้วค่อยขยายวิธีชำระเงิน */}
                <button
                  type="button"
                  onClick={() => setShowPaymentMethods((v) => !v)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-4 rounded-xl border-2 border-[#BEE3DE] bg-[#EAF6F5] hover:border-[#00B4A1] hover:shadow-md active:scale-[0.99] transition-all text-left"
                >
                  <span className="flex items-center gap-3 min-w-0">
                    <span className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00B4A1] to-[#3A93B9] flex items-center justify-center flex-shrink-0 shadow">
                      <CreditCard className="w-5 h-5 text-white" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-base md:text-lg font-bold text-gray-900">เลือกวิธีการชำระเงิน</span>
                      {!showPaymentMethods && (
                        <span className="block text-xs text-gray-500 mt-0.5">แตะเพื่อเลือกวิธีชำระเงินและใส่รหัสส่วนลด</span>
                      )}
                    </span>
                  </span>
                  <ChevronDown className={`w-6 h-6 text-[#00B4A1] flex-shrink-0 transition-transform ${showPaymentMethods ? 'rotate-180' : ''}`} />
                </button>

                {showPaymentMethods && (
                <div className="mt-6">
                {/* Payment Method Selector */}
                <PaymentMethodSelector
                  selectedMethod={paymentMethod}
                  onMethodChange={setPaymentMethod}
                />

                {/* Error Display */}
                {error && (
                  <div className="mt-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                {/* Discount Code Section */}
                <div className="mt-6 bg-[#EEF0FB] rounded-2xl p-6 border-2 border-[#D2D8F2]">
                  <div className="flex items-center gap-2 mb-4">
                    <Tag className="w-5 h-5 text-[#5B6FCB]" />
                    <h3 className="text-lg font-bold text-gray-900">รหัสส่วนลด</h3>
                  </div>
                  
                  {!discount ? (
                    <>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={discountCode}
                          onChange={(e) => {
                            setDiscountCode(e.target.value.toUpperCase());
                            setDiscountError('');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && discountCode.trim() && !applyingDiscount) {
                              e.preventDefault();
                              applyDiscount();
                            }
                          }}
                          placeholder="ใส่รหัสส่วนลด"
                          className="flex-1 px-4 py-3 border-2 border-[#D2D8F2] rounded-xl focus:outline-none focus:border-purple-500 transition-colors bg-white"
                          disabled={applyingDiscount}
                        />
                        <button
                          onClick={applyDiscount}
                          disabled={applyingDiscount || !discountCode.trim()}
                          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {applyingDiscount ? 'กำลังตรวจสอบ...' : 'ใช้รหัส'}
                        </button>
                      </div>
                      
                      {discountError && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2">
                          <AlertCircle className="w-5 h-5 text-red-600" />
                          <span className="text-red-700 text-sm">{discountError}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="space-y-3">
                      <div className="p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <span className="font-bold text-green-700">ใช้รหัสส่วนลดสำเร็จ!</span>
                          </div>
                          <button
                            onClick={removeDiscount}
                            className="text-red-600 hover:text-red-700 text-sm font-semibold"
                          >
                            ลบ
                          </button>
                        </div>
                        <div className="text-sm text-gray-700">
                          <p className="font-semibold">รหัส: {discount.code}</p>
                          <p className="text-gray-600">{discount.description}</p>
                          <p className="font-bold text-green-600 mt-1">
                            ลด {discount.type === 'percentage' ? `${discount.value}%` : `฿${discount.value}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Payment Content */}
                <div className="mt-6">
                  {/* Check if this is Free plan (totalAmount === 0 and no discount) */}
                  {totalAmount === 0 && !discount ? (
                    <div className="bg-[#EAF6F5] border-2 border-[#BEE3DE] rounded-xl p-5 md:p-8">
                      <div className="text-center mb-6">
                        <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Sparkles className="w-12 h-12 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                          ทดลองใช้ฟรี 14 วัน!
                        </h3>
                        <p className="text-gray-600">ไม่ต้องชำระเงิน ไม่ต้องใช้บัตรเครดิต</p>
                      </div>
                      
                      <div className="bg-white rounded-lg p-4 mb-6">
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-600">แพ็กเกจ:</span>
                          <span className="font-bold text-gray-900">{selectedPlan.name}</span>
                        </div>
                        <div className="border-t pt-2 mt-2">
                          <div className="flex justify-between">
                            <span className="text-lg font-bold">ยอดชำระ:</span>
                            <span className="text-2xl font-bold text-[#00B4A1]">ฟรี</span>
                          </div>
                        </div>
                      </div>
                      
                      {!user ? (
                        <button
                          onClick={() => {
                            // Determine source domain
                            const hostname = window.location.hostname;
                            let sourceDomain = 'edumi';
                            if (hostname.includes('aiforyou')) {
                              sourceDomain = 'coursedee';
                            } else if (hostname.includes('salepage')) {
                              sourceDomain = 'salepage';
                            }
                            
                            // Redirect to register info page
                            navigate(`/subscription/register-info?plan=free&source=${sourceDomain}`);
                          }}
                          className="w-full py-4 bg-gradient-to-r from-[#00B4A1] to-[#3A93B9] hover:from-[#0E7C86] hover:to-[#2E7A99] text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                        >
                          <CheckCircle className="w-5 h-5" />
                          เริ่มใช้งานฟรีทันที
                        </button>
                      ) : (
                        <button
                          onClick={handleFreeActivation}
                          disabled={activatingFree}
                          className="w-full py-4 bg-gradient-to-r from-[#00B4A1] to-[#3A93B9] hover:from-[#0E7C86] hover:to-[#2E7A99] text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {activatingFree ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              กำลังเปิดใช้งาน...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-5 h-5" />
                              เปิดใช้งานฟรีทันที
                            </>
                          )}
                        </button>
                      )}
                      
                      <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mt-4">
                        <Lightbulb className="w-4 h-4 text-amber-500" />
                        <p>ไม่ต้องชำระเงิน ไม่ต้องรอการอนุมัติ เริ่มใช้งานทันที</p>
                      </div>
                    </div>
                  ) : discount && discount.type === 'percentage' && discount.value === 100 ? (
                    /* Check if this is 100% discount (Free Activation) */
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-5 md:p-8">
                      <div className="text-center mb-6">
                        <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Sparkles className="w-12 h-12 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                          คุณได้รับส่วนลด 100%!
                        </h3>
                        <p className="text-gray-600">ไม่ต้องชำระเงิน เปิดใช้งานได้เลย</p>
                      </div>
                      
                      <div className="bg-white rounded-lg p-4 mb-6">
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-600">ราคาเดิม:</span>
                          <span className="line-through text-gray-400">
                            ฿{(checkoutData?.planPrice || selectedPlan.price || 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-600">ส่วนลด:</span>
                          <span className="text-green-600 font-bold">
                            -฿{(checkoutData?.planPrice || selectedPlan.price || 0).toLocaleString()} (100%)
                          </span>
                        </div>
                        <div className="border-t pt-2 mt-2">
                          <div className="flex justify-between">
                            <span className="text-lg font-bold">ยอดชำระ:</span>
                            <span className="text-2xl font-bold text-green-600">฿0</span>
                          </div>
                        </div>
                      </div>
                      
                      <button
                        onClick={handleFreeActivation}
                        disabled={activatingFree}
                        className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {activatingFree ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            กำลังเปิดใช้งาน...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-5 h-5" />
                            เปิดใช้งานฟรีทันที
                          </>
                        )}
                      </button>
                      
                      <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mt-4">
                        <Lightbulb className="w-4 h-4 text-amber-500" />
                        <p>ไม่ต้องชำระเงิน ไม่ต้องรอการอนุมัติ เปิดใช้งานทันที</p>
                      </div>
                    </div>
                  ) : (
                    /* Regular payment flow */
                    <>
                      {paymentMethod === 'qr' ? (
                        <PromptPayQRPayment
                          amount={totalAmount > 0 ? totalAmount : selectedPlan.price}
                          planName={selectedPlan.name}
                          planTier={selectedPlan.tier}
                          promptPayPhone="0909532495"
                          onPaymentSuccess={handlePaymentSuccess}
                          onPaymentError={handlePaymentError}
                          additionalData={checkoutData}
                        />
                      ) : (
                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-5 md:p-8 text-center">
                          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertCircle className="w-12 h-12 text-amber-600" />
                          </div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-3">เร็วๆ นี้!</h3>
                          <p className="text-gray-700 mb-4">
                            ระบบชำระเงินด้วยบัตรเครดิต/เดบิตกำลังพัฒนา
                          </p>
                          <p className="text-sm text-gray-600">
                            กรุณาใช้ QR PromptPay สำหรับการชำระเงินในขณะนี้
                          </p>
                          <button
                            onClick={() => setPaymentMethod('qr')}
                            className="mt-6 px-6 py-3 bg-gradient-to-r from-[#00B4A1] to-[#3A93B9] text-white rounded-xl font-semibold hover:shadow-xl transition-all"
                          >
                            ใช้ QR PromptPay แทน
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
